import React, { useState, useEffect } from 'react';
import { serviceApi } from '../api';

const SERVICE_TYPES = [
  'Wheel Bearing',
  'Brake Inspection',
  'Lights Check',
  'Chains',
  'Breakaway Cable',
  'Lug Torque',
  'Full Service',
  'Routine Check',
  'Tire Service',
  'Initial Inspection',
  'Other',
];

const API_URL = process.env.REACT_APP_API_URL || '';

const blankService = () => ({
  id: Date.now() + Math.random(),
  service_type: '',
  custom_type: '',
  description_of_work: '',
  parts_used: '',
});

// Timezone-safe date formatter — treats date strings as local, not UTC
// Prevents "03/27/2026" from displaying as "03/26/2026" in US timezones
function formatDate(dateStr) {
  if (!dateStr) return '—';
  const [year, month, day] = String(dateStr).split('T')[0].split('-');
  return `${month}/${day}/${year}`;
}

function generatePDF(entries, trailerName) {
  const rows = entries.map(e => `
    <tr>
      <td>${formatDate(e.performed_date)}</td>
      <td>${e.tracker_name && e.tracker_name !== 'Unassigned' ? e.tracker_name : '—'}</td>
      <td>${e.mileage_at_service || '—'}</td>
      <td><strong>${e.service_type || '—'}</strong></td>
      <td>${e.description_of_work || '—'}</td>
      <td>${e.parts_used || '—'}</td>
      <td>${e.performed_by || '—'}</td>
      <td>${e.notes || '—'}</td>
    </tr>
  `).join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Service Log — ${trailerName || 'All Trailers'}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: -apple-system, sans-serif; font-size: 11px; color: #1e293b; padding: 32px; }
        .header { border-bottom: 2px solid #f97316; padding-bottom: 16px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: flex-end; }
        .header h1 { font-size: 22px; font-weight: 700; color: #f97316; }
        .header .subtitle { font-size: 13px; color: #64748b; margin-top: 4px; }
        .meta { font-size: 11px; color: #94a3b8; text-align: right; }
        table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        th { background: #f97316; color: white; padding: 8px 10px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
        td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
        tr:nth-child(even) td { background: #f8fafc; }
        tr:last-child td { border-bottom: none; }
        .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; text-align: center; }
        @media print {
          body { padding: 16px; }
          @page { margin: 0.5in; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <h1>Service Log</h1>
          <div class="subtitle">${trailerName || 'All Trailers'} &nbsp;·&nbsp; ${entries.length} record${entries.length !== 1 ? 's' : ''}</div>
        </div>
        <div class="meta">
          Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Trailer</th>
            <th>Mileage / Use</th>
            <th>Service Type</th>
            <th>Description of Work</th>
            <th>Parts Used</th>
            <th>Technician</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">RentFlow Fleet Management &nbsp;·&nbsp; Confidential</div>
    </body>
    </html>
  `;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 500);
}

export default function ServiceLog({ subAccountId, trackers, selectedTracker, onSelectTracker }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterTrackerId, setFilterTrackerId] = useState('all');

  const [form, setForm] = useState({
    tracker_id: '',
    tracker_name: '',
    services: [blankService()],
    performed_date: new Date().toISOString().split('T')[0],
    mileage_at_service: '',
    performed_by: '',
    notes: '',
  });

  const loadLog = async () => {
    setLoading(true);
    try {
      const tid = filterTrackerId === 'all' ? null : filterTrackerId;
      const data = await serviceApi.getLog(subAccountId, tid);
      setEntries(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLog(); }, [filterTrackerId, subAccountId]);

  const openNewForm = () => {
    setEditingEntry(null);
    const t = selectedTracker || trackers[0];
    setForm({
      tracker_id: t?.id || '',
      tracker_name: t?.name || '',
      services: [blankService()],
      performed_date: new Date().toISOString().split('T')[0],
      mileage_at_service: t?.total_miles ? Math.round(t.total_miles).toString() : '',
      performed_by: '',
      notes: '',
    });
    setShowForm(true);
  };

  const openEditForm = (entry) => {
    setEditingEntry(entry);
    const isCustom = !SERVICE_TYPES.includes(entry.service_type);
    setForm({
      tracker_id: entry.tracker_id || '',
      tracker_name: entry.tracker_name || '',
      services: [{
        id: 1,
        service_type: isCustom ? 'Other' : (entry.service_type || ''),
        custom_type: isCustom ? entry.service_type : '',
        description_of_work: entry.description_of_work || '',
        parts_used: entry.parts_used || '',
      }],
      performed_date: (entry.performed_date || '').split('T')[0],
      mileage_at_service: entry.mileage_at_service || '',
      performed_by: entry.performed_by || '',
      notes: entry.notes || '',
    });
    setShowForm(true);
  };

  const updateService = (id, field, value) => {
    setForm(f => ({
      ...f,
      services: f.services.map(s => s.id === id ? { ...s, [field]: value } : s),
    }));
  };

  const addService = () => setForm(f => ({ ...f, services: [...f.services, blankService()] }));
  const removeService = (id) => setForm(f => ({ ...f, services: f.services.filter(s => s.id !== id) }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.performed_date) { alert('Date is required.'); return; }
    const validServices = form.services.filter(s => s.service_type);
    if (validServices.length === 0) { alert('Please select at least one service type.'); return; }
    setSaving(true);
    try {
      for (const svc of validServices) {
        const serviceType = svc.service_type === 'Other' ? (svc.custom_type || 'Other') : svc.service_type;
        const payload = {
          tracker_id: form.tracker_id || null,
          tracker_name: form.tracker_name || null,
          service_type: serviceType,
          description_of_work: svc.description_of_work || null,
          parts_used: svc.parts_used || null,
          performed_date: form.performed_date,
          mileage_at_service: form.mileage_at_service || null,
          performed_by: form.performed_by || null,
          notes: form.notes || null,
        };
        if (editingEntry && validServices.length === 1) {
          await serviceApi.update(subAccountId, editingEntry.id, payload);
        } else {
          await serviceApi.add(subAccountId, payload);
        }
      }
      setShowForm(false);
      await loadLog();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service entry?')) return;
    await serviceApi.remove(subAccountId, id).catch(() => {});
    await loadLog();
  };

  const handlePDF = () => {
    const trailerName = filterTrackerId === 'all'
      ? 'All Trailers'
      : trackers.find(t => t.id === filterTrackerId)?.name || 'All Trailers';
    generatePDF(entries, trailerName);
  };

  const inputStyle = {
    padding: '8px 10px', border: '1px solid var(--slate-200)',
    borderRadius: '6px', fontSize: '13px', width: '100%',
    background: 'white', color: 'var(--slate-800)',
  };
  const labelStyle = {
    fontSize: '12px', fontWeight: '600', color: 'var(--slate-600)',
    marginBottom: '5px', display: 'block',
  };

  return (
    <div className="alerts-panel">
      <div className="alerts-header">
        <div>
          <h2>Service Log</h2>
          <p>Complete maintenance history — exportable anytime.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {entries.length > 0 && (
            <>
              <button className="btn-ghost" onClick={handlePDF} style={{ fontSize: '13px' }}>
                Print / PDF
              </button>
              <a
                href={`${API_URL}/api/service-log/export?sub_account_id=${subAccountId}${filterTrackerId !== 'all' ? `&tracker_id=${filterTrackerId}` : ''}`}
                className="btn-ghost"
                style={{ textDecoration: 'none', padding: '8px 14px', fontSize: '13px' }}
                download
              >
                Export CSV
              </a>
            </>
          )}
          <button className="btn-primary" onClick={openNewForm}>+ Log Service</button>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>Show:</label>
        <select
          value={filterTrackerId}
          onChange={e => setFilterTrackerId(e.target.value)}
          style={{ ...inputStyle, width: 'auto' }}
        >
          <option value="all">All trailers</option>
          {trackers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {/* Form */}
      {showForm && (
        <div className="alert-form-card">
          <h3>{editingEntry ? 'Edit Entry' : 'Log Service'}</h3>
          <form onSubmit={handleSave}>
            <div className="form-row">
              <div className="form-group">
                <label style={labelStyle}>Trailer</label>
                <select
                  value={form.tracker_id}
                  onChange={e => {
                    const t = trackers.find(t => t.id === e.target.value);
                    setForm(f => ({ ...f, tracker_id: e.target.value, tracker_name: t?.name || '' }));
                  }}
                  style={inputStyle}
                >
                  <option value="">No tracker / unassigned</option>
                  {trackers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label style={labelStyle}>Date performed *</label>
                <input type="date" value={form.performed_date}
                  onChange={e => setForm(f => ({ ...f, performed_date: e.target.value }))}
                  style={inputStyle} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label style={labelStyle}>Mileage / Use</label>
                <input type="text" value={form.mileage_at_service}
                  onChange={e => setForm(f => ({ ...f, mileage_at_service: e.target.value }))}
                  placeholder="e.g. 12,500 mi or 45 rentals" style={inputStyle} />
              </div>
              <div className="form-group">
                <label style={labelStyle}>Performed by</label>
                <input type="text" value={form.performed_by}
                  onChange={e => setForm(f => ({ ...f, performed_by: e.target.value }))}
                  placeholder="Name or shop" style={inputStyle} />
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--slate-200)', paddingTop: '16px', marginTop: '8px' }}>
              <label style={{ ...labelStyle, marginBottom: '12px' }}>Services performed</label>
              {form.services.map((svc, idx) => (
                <div key={svc.id} style={{
                  background: 'var(--slate-50)', border: '1px solid var(--slate-200)',
                  borderRadius: '8px', padding: '14px', marginBottom: '12px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--slate-500)' }}>Service {idx + 1}</span>
                    {form.services.length > 1 && (
                      <button type="button" onClick={() => removeService(svc.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--slate-400)', fontSize: '16px' }}>✕</button>
                    )}
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label style={labelStyle}>Service type</label>
                      <select value={svc.service_type}
                        onChange={e => updateService(svc.id, 'service_type', e.target.value)} style={inputStyle}>
                        <option value="">Select...</option>
                        {SERVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      {svc.service_type === 'Other' && (
                        <input type="text" value={svc.custom_type}
                          onChange={e => updateService(svc.id, 'custom_type', e.target.value)}
                          placeholder="Describe the service..."
                          style={{ ...inputStyle, marginTop: '8px' }} />
                      )}
                    </div>
                    <div className="form-group">
                      <label style={labelStyle}>Parts used</label>
                      <input type="text" value={svc.parts_used}
                        onChange={e => updateService(svc.id, 'parts_used', e.target.value)}
                        placeholder="e.g. Lucas Red Grease" style={inputStyle} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label style={labelStyle}>Description of work</label>
                    <textarea value={svc.description_of_work}
                      onChange={e => updateService(svc.id, 'description_of_work', e.target.value)}
                      placeholder="e.g. Repacked bearings, checked for wear"
                      rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addService} style={{
                width: '100%', padding: '10px', border: '1px dashed var(--slate-300)',
                borderRadius: '8px', background: 'none', cursor: 'pointer',
                color: 'var(--slate-500)', fontSize: '13px', fontWeight: '500', marginBottom: '16px',
              }}>+ Add another service</button>
            </div>

            <div className="form-group" style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Additional notes</label>
              <textarea value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Any other details for this service visit..."
                rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>

            <div className="form-actions">
              <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving…' : `Save ${form.services.filter(s => s.service_type).length > 1 ? form.services.filter(s => s.service_type).length + ' entries' : 'entry'}`}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="loading-screen"><div className="spinner"/></div>
      ) : entries.length === 0 && !showForm ? (
        <div className="empty-state">
          <span className="empty-icon">🔧</span>
          <h3>No service entries yet</h3>
          <p>Start logging maintenance to build a complete service history.</p>
          <button className="btn-primary" onClick={openNewForm}>Log First Service</button>
        </div>
      ) : entries.length > 0 ? (
        <div style={{ background: 'white', borderRadius: '8px', border: '1px solid var(--slate-200)', overflow: 'auto' }}>
          <table className="history-table" style={{ width: '100%', minWidth: '700px' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Trailer</th>
                <th>Mileage / Use</th>
                <th>Service Type</th>
                <th>Description of Work</th>
                <th>Parts Used</th>
                <th>Technician</th>
                <th style={{ width: '60px' }}></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {formatDate(entry.performed_date)}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '12px', color: 'var(--slate-500)' }}>
                    {entry.tracker_name && entry.tracker_name !== 'Unassigned' ? entry.tracker_name : '—'}
                  </td>
                  <td>{entry.mileage_at_service || '—'}</td>
                  <td><strong>{entry.service_type}</strong></td>
                  <td style={{ maxWidth: '200px' }}>{entry.description_of_work || '—'}</td>
                  <td>{entry.parts_used || '—'}</td>
                  <td>{entry.performed_by || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <button className="btn-icon" onClick={() => openEditForm(entry)} title="Edit">✏️</button>
                      <button className="btn-icon danger" onClick={() => handleDelete(entry.id)} title="Delete">🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
